import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./Login.css";
import person_icon from "../../Assets/person.png";
import contact_icon from "../../Assets/contact.png";
import password_icon from "../../Assets/password.png";
import { acceptInvitationAsync } from "../../api/UserApi";
import { ResponseMessage } from "../../models/ResponseMessage";
import { User } from "../../models/User";
import { getFieldValidationErrors } from "../../models/ValidationProblemDetails";
import { usePasswordValidation } from "../../hooks/usePasswordValidation";
import { PASSWORD_POLICY_ERROR_MESSAGE } from "../../utils/passwordPolicy";
import Toast from "../../components/Toast";
import PasswordRequirements from "../../components/PasswordRequirements";

interface AcceptInvitationProps {}

const AcceptInvitation: React.FC<AcceptInvitationProps> = () => {
  const [userName, setUserName] = useState("");
  const [contact, setContact] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const {
    checks,
    checklistId,
    isValid,
    touched,
    showError,
    handleBlur,
    reset,
  } = usePasswordValidation(password);

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const navigate = useNavigate();

  if (!token) {
    return (
      <div className="container">
        <div className="holder_login">
          <div className="inputs">
            <label className="name">Invalid Link</label>
            <p className="incorrect_message">
              This invitation link is invalid or expired.
            </p>
            <button className="login-btn" onClick={() => navigate("/login")}>
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleAcceptInvitation = async () => {
    if (!password || !confirmPassword) {
      setToast({ message: "Please fill all the fields", type: "error" });
      return;
    }

    if (password !== confirmPassword) {
      setToast({ message: "Passwords do not match", type: "error" });
      return;
    }

    handleBlur();

    if (!isValid) {
      setToast({ message: PASSWORD_POLICY_ERROR_MESSAGE, type: "error" });
      return;
    }

    setLoading(true);
    try {
      const response: ResponseMessage<User> = await acceptInvitationAsync(
        token,
        password,
        userName || undefined,
        contact || undefined
      );

      if (response.success) {
        setLoading(false);
        setToast({
          message: "Account activated. Please log in.",
          type: "success",
        });
        navigate("/login");
      } else {
        setLoading(false);
        setToast({
          message: response.message || "Failed to accept invitation.",
          type: "error",
        });
      }
    } catch (error) {
      setLoading(false);
      const validationErrors = getFieldValidationErrors(error, {
        Password: "password",
      });
      if (Object.keys(validationErrors).length > 0) {
        setFieldErrors(validationErrors);
        setToast({ message: PASSWORD_POLICY_ERROR_MESSAGE, type: "error" });
      } else {
        setToast({ message: "Network error, please try again.", type: "error" });
      }
    }
  };

  return (
    <div className="container">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className="holder_login">
        <div className="inputs">
          {loading && (
            <div className="loading-overlay">
              <div className="spinner"></div>
            </div>
          )}
          <label className="name">Accept Invitation</label>

          <div className="input-wrapper">
            <img className="input-icon" src={person_icon} alt="person icon" />
            <input
              className="input"
              placeholder="Name (optional)"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
            />
          </div>

          <div className="input-wrapper">
            <img className="input-icon" src={contact_icon} alt="contact icon" />
            <input
              className="input"
              placeholder="Contact (optional)"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
            />
          </div>

          <div className="input-wrapper">
            <img
              className="input-icon"
              src={password_icon}
              alt="password icon"
            />
            <input
              className="input"
              type="password"
              placeholder="Password"
              value={password}
              maxLength={128}
              aria-invalid={showError || !!fieldErrors.password}
              aria-describedby={checklistId}
              onChange={(e) => {
                setPassword(e.target.value);
                setFieldErrors((prev) => {
                  const next = { ...prev };
                  delete next.password;
                  return next;
                });
              }}
            />
          </div>
          <PasswordRequirements checks={checks} id={checklistId} />
          {(showError || fieldErrors.password) && (
            <p className="password-field-error" role="alert">
              {fieldErrors.password ?? PASSWORD_POLICY_ERROR_MESSAGE}
            </p>
          )}

          <div className="input-wrapper">
            <img
              className="input-icon"
              src={password_icon}
              alt="password icon"
            />
            <input
              className="input"
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>

          <div className="login-create-account">
            <button className="login-btn" onClick={handleAcceptInvitation}>
              Accept Invitation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitation;